import { useState } from "react";
import { Download, Flame, Phone, Search } from "lucide-react";
import { AppShell } from "@/shared/layouts/AppShell.jsx";
import { Card } from "@/shared/ui/card.jsx";
import { Badge } from "@/shared/ui/badge.jsx";
import { Button } from "@/shared/ui/button.jsx";
import { Input } from "@/shared/ui/input.jsx";
import { Spinner, EmptyState } from "@/shared/ui/spinner.jsx";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/shared/ui/table.jsx";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/shared/ui/select.jsx";
import { initials, formatDateTime } from "@/shared/lib/format.js";
import { useDebounced } from "@/shared/hooks/use-debounced.js";
import { downloadCsv } from "@/shared/lib/csv.js";
import { LeadDrawer } from "./components/LeadDrawer.jsx";
import { CreateLeadDialog } from "./components/CreateLeadDialog.jsx";
import { useLeads, useLeadOrigins } from "./use-leads.js";
import { STAGES, stageById, temperatureLabel, temperatureStyle } from "./lead-constants.js";

/**
 * Lista de leads.
 *
 * A filtragem acontece no servidor. A tela antiga baixava todos os leads e
 * filtrava no navegador com `useMemo` — o que significa transferir a base
 * inteira para digitar uma letra na busca.
 */
export function LeadsPage() {
  const [busca, setBusca] = useState("");
  const [origem, setOrigem] = useState("todos");
  const [status, setStatus] = useState("todos");
  const [leadAberto, setLeadAberto] = useState(null);

  // Espera o usuário parar de digitar antes de consultar o servidor.
  const buscaAdiada = useDebounced(busca, 300);

  const { data: leads = [], isLoading, isFetching } = useLeads({
    search: buscaAdiada,
    origin: origem,
    status,
  });
  const { data: origens = [] } = useLeadOrigins();

  function exportar() {
    downloadCsv(
      "leads.csv",
      leads.map((l) => ({
        Nome: l.name,
        Telefone: l.phone,
        Origem: l.origin,
        Campanha: l.campaign,
        Etapa: stageById(l.status).title,
        Temperatura: temperatureLabel(l.temperature),
        Responsável: l.owner,
        Procedimento: l.procedure,
        Entrada: formatDateTime(l.createdAt),
      })),
    );
  }

  return (
    <AppShell
      title="Leads"
      subtitle={`${leads.length} ${leads.length === 1 ? "lead encontrado" : "leads encontrados"}`}
      actions={<CreateLeadDialog />}
    >
      <div className="space-y-4 p-6">
        {/* ------------------------------------------------------ filtros */}
        <Card className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative min-w-60 flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder="Buscar por nome ou telefone..."
                className="pl-9"
                aria-label="Buscar leads"
              />
              {isFetching && !isLoading && (
                <Spinner className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              )}
            </div>

            <Select value={origem} onValueChange={setOrigem}>
              <SelectTrigger className="w-44">
                <SelectValue placeholder="Origem" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todas as origens</SelectItem>
                {origens.map((o) => (
                  <SelectItem key={o} value={o}>
                    {o}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-52">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos os status</SelectItem>
                {STAGES.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={exportar}
              disabled={leads.length === 0}
            >
              <Download className="h-4 w-4" />
              Exportar
            </Button>
          </div>
        </Card>

        {/* ------------------------------------------------------- tabela */}
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-t-0 hover:bg-transparent">
                <TableHead>Lead</TableHead>
                <TableHead>Telefone</TableHead>
                <TableHead>Origem / Campanha</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Temp.</TableHead>
                <TableHead>Responsável</TableHead>
                <TableHead>Entrada</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="py-8 text-center">
                    <div className="flex flex-col items-center gap-2 text-muted-foreground">
                      <Spinner className="h-6 w-6" />
                      <p>Carregando leads...</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : leads.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <EmptyState
                      icon={Search}
                      title="Nenhum lead encontrado"
                      description="Ajuste os filtros ou cadastre um novo lead."
                    />
                  </TableCell>
                </TableRow>
              ) : (
                leads.map((lead) => {
                  const etapa = stageById(lead.status);
                  return (
                    <TableRow
                      key={lead.id}
                      className="cursor-pointer"
                      onClick={() => setLeadAberto(lead)}
                      tabIndex={0}
                      onKeyDown={(e) => e.key === "Enter" && setLeadAberto(lead)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary/20 to-chart-2/20 text-xs font-semibold">
                            {initials(lead.name)}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium leading-tight">{lead.name}</p>
                            <p className="truncate text-xs text-muted-foreground">
                              {[lead.procedure, lead.summary].filter(Boolean).join(" · ").slice(0, 48)}
                            </p>
                          </div>
                        </div>
                      </TableCell>

                      <TableCell className="text-muted-foreground">
                        {lead.phone ? (
                          <span className="inline-flex items-center gap-1.5">
                            <Phone className="h-3 w-3" />
                            {lead.phone}
                          </span>
                        ) : (
                          "—"
                        )}
                      </TableCell>

                      <TableCell>
                        <p className="text-xs font-medium">{lead.origin || "—"}</p>
                        <p className="text-xs text-muted-foreground">{lead.campaign}</p>
                      </TableCell>

                      <TableCell>
                        <Badge variant="secondary" className={etapa.tone}>
                          {etapa.title}
                        </Badge>
                      </TableCell>

                      <TableCell>
                        <span
                          className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium ${temperatureStyle(lead.temperature)}`}
                        >
                          <Flame className="h-3 w-3" />
                          {temperatureLabel(lead.temperature)}
                        </span>
                      </TableCell>

                      <TableCell className="text-xs text-muted-foreground">
                        {lead.owner || "Sem responsável"}
                      </TableCell>

                      <TableCell className="text-xs text-muted-foreground">
                        {formatDateTime(lead.lastInteractionAt ?? lead.createdAt)}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Card>
      </div>

      {/* O mesmo componente usado pelo CRM. */}
      <LeadDrawer
        lead={leadAberto ? (leads.find((l) => l.id === leadAberto.id) ?? leadAberto) : null}
        onClose={() => setLeadAberto(null)}
      />
    </AppShell>
  );
}
